import { useMemo } from 'react';
import { createTheme } from '@mui/material/styles';
import { useThemeContext } from '../context/ThemeContext';
import { APP_ACCENT, APP_ACCENT_DARK_MODE } from '../config/env';
 
export const TOKENS = {
  primary: APP_ACCENT,
  primaryDark: '#047857',
  primaryLight: '#d1fae5',
  success: '#16915a',
  successBg: '#e9f8f0',
  warning: '#f0770a',
  warningBg: '#fff6e5',
  warningText: '#8a5800',
  error: '#dc2626',
  errorBg: '#fdecec',
  purple: '#7c3aed',
  purpleBg: '#f4eefe',
  teal: '#0d9488',
  tealBg: '#e2f6f4',
  indigo: '#4338ca',
  indigoBg: '#ecebfd',
  cyan: '#0e7490',
  cyanBg: '#e1f5fa',
  textPrimary: '#161b26',
  textSecondary: '#5f6b80',
  textDisabled: '#9aa4b8',
  divider: '#e7eaf1',
  border: '#e1e5ee',
  bg: '#f3f5fa',
  paper: '#ffffff',
};
 
export const DARK_TOKENS = {
  bg: '#0f172a',
  paper: '#1e293b',
  divider: '#334155',
  tableHead: '#28354f',
  tableHeadText: '#e2e8f0',
};
 
export const SHADOWS = {
  e1: '0 1px 2px rgba(20,30,55,.05), 0 1px 3px rgba(20,30,55,.07)',
  e2: '0 3px 8px rgba(20,30,55,.07), 0 1px 3px rgba(20,30,55,.06)',
  e3: '0 8px 24px rgba(20,30,55,.12)',
  hover: '0 6px 16px rgba(30,111,224,.14), 0 2px 6px rgba(20,30,55,.08)',
};
 
export const SCOPE_TOKENS = {
  TOTAL:   { color: '#7c3aed', bg: '#f4eefe', dark: '#5b21b6', label: 'Total' },
  SCOPE_1: { color: '#1e6fe0', bg: '#eaf2ff', dark: '#144fb0', label: 'Scope 1' },
  SCOPE_2: { color: '#f0770a', bg: '#fef1e3', dark: '#c25a00', label: 'Scope 2' },
  SCOPE_3: { color: '#16915a', bg: '#e7f7ee', dark: '#0e6b41', label: 'Scope 3' },
};
 
export const CHART_SERIES = {
  light: { TOTAL: '#10b981', SCOPE_1: '#3b82f6', SCOPE_2: '#f97316', SCOPE_3: '#22c55e' },
  dark: { TOTAL: '#34d399', SCOPE_1: '#60a5fa', SCOPE_2: '#fb923c', SCOPE_3: '#4ade80' },
};
 
export const chartSeriesFor = (isDark) => (isDark ? CHART_SERIES.dark : CHART_SERIES.light);
 
export const GREEN_TOKEN = { color: '#0f9d58', bg: '#e6f6ed', dark: '#0b7a43' };
 
/**
 * Microsoft Excel's own green — for anything that represents a workbook, so a
 * spreadsheet is recognisable as one before the file name is even read.
 * Deliberately not `GREEN_TOKEN`: that one means "green energy" on the Office
 * screen, and two unrelated meanings must not share a colour.
 */
export const EXCEL_TOKEN = { color: '#217346', bg: '#e8f3ec', dark: '#185c37' };
 
export const MONO_FONT = "'IBM Plex Mono', ui-monospace, Menlo, Consolas, monospace";
 
/**
 * Corner radii for input controls. `shape.borderRadius` is 12 — right for
 * cards and dialogs, far too round for a text field or a header filter, so
 * controls opt out with these instead of a `shape` multiple.
 */
export const RADIUS = {
  control: 8,   // form inputs, toolbar search
  filter: 6,    // dense per-column filters in a table header
};
 
/**
 * Data-table surfaces. `palette.divider` is tuned for card edges and is far
 * too faint to separate table rows, so the grid gets its own scale: a quiet
 * header band, a firm rule closing the header, and a visible row divider.
 */
export const TABLE_TOKENS = {
  light: { headBg: '#eef1f7', headRule: '#cfd7e6', rowRule: '#e4e9f2' },
  dark: { headBg: '#28354f', headRule: '#42536f', rowRule: '#2f3d57' },
};
 
export const tableTokens = (theme) => (
  theme.palette.mode === 'dark' ? TABLE_TOKENS.dark : TABLE_TOKENS.light
);
 
export const SCOPE_CHIP_SX = {
  SCOPE_1: { bgcolor: SCOPE_TOKENS.SCOPE_1.bg, color: SCOPE_TOKENS.SCOPE_1.dark, fontWeight: 700 },
  SCOPE_2: { bgcolor: SCOPE_TOKENS.SCOPE_2.bg, color: SCOPE_TOKENS.SCOPE_2.dark, fontWeight: 700 },
  SCOPE_3: { bgcolor: SCOPE_TOKENS.SCOPE_3.bg, color: SCOPE_TOKENS.SCOPE_3.dark, fontWeight: 700 },
};
 
const AVATAR_SATURATION = 60;
const AVATAR_LIGHTNESS = 50;
 
export function avatarColorFor(seed = '') {
  return `hsl(${(seed.length * 40) % 360}, ${AVATAR_SATURATION}%, ${AVATAR_LIGHTNESS}%)`;
}
// Builds the MUI palette and component tokens for the active light or dark mode.
export function useMuiTheme() {
  const { dark } = useThemeContext();
 
  return useMemo(() => createTheme({
    palette: {
      mode: dark ? 'dark' : 'light',
      primary: {
        main: dark ? APP_ACCENT_DARK_MODE : TOKENS.primary,
        dark: TOKENS.primaryDark,
        light: TOKENS.primaryLight,
      },
      success: { main: TOKENS.success },
      warning: { main: TOKENS.warning },
      error: { main: TOKENS.error },
      background: dark
        ? { default: DARK_TOKENS.bg, paper: DARK_TOKENS.paper }
        : { default: TOKENS.bg, paper: TOKENS.paper },
      divider: dark ? DARK_TOKENS.divider : TOKENS.divider,
      ...(dark ? {} : {
        text: { primary: TOKENS.textPrimary, secondary: TOKENS.textSecondary, disabled: TOKENS.textDisabled },
      }),
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      fontSize: 13,
      kpiPrimary:   { fontSize: 48, fontWeight: 600, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' },
      kpiSecondary: { fontSize: 32, fontWeight: 600, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' },
      h6:      { fontSize: 20, fontWeight: 600 },
      body2:   { fontSize: 14 },
      caption: { fontSize: 12 },
      button:  { fontSize: 13, fontWeight: 600 },
    },
    components: {
      MuiTypography: {
        defaultProps: {
          variantMapping: { kpiPrimary: 'div', kpiSecondary: 'div' },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { textTransform: 'none', fontWeight: 600, borderRadius: 9 } },
      },
      MuiChip: { styleOverrides: { root: { borderRadius: 99, fontWeight: 600 } } },
      MuiDialog: { defaultProps: { disableScrollLock: true } },
      MuiDialogActions: { styleOverrides: { root: { padding: '8px 24px' } } },
      MuiPaper: { styleOverrides: { rounded: { borderRadius: 12 } } },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${dark ? DARK_TOKENS.divider : TOKENS.border}`,
            boxShadow: dark ? 'none' : SHADOWS.e1,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontSize: 11.5,
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            whiteSpace: 'nowrap',
            backgroundColor: dark ? DARK_TOKENS.tableHead : '#e8edf4',
            color: dark ? DARK_TOKENS.tableHeadText : '#1e293b',
          },
        },
      },
      MuiMenu: { defaultProps: { disableScrollLock: true } },
      MuiTooltip: { defaultProps: { arrow: true } },
    },
  }), [dark]);
}
 
